import React, { useCallback, type FC } from 'react'

import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue } from 'recoil'

import { Select } from 'mailup-web-components'

import { changeSearchCallback } from 'Pages/GroupsOverlap/recoil/callbacks/callbacks'
import { searchState } from 'Pages/GroupsOverlap/recoil/state/state'
import {
  fetchGroupById,
  fetchGroups,
} from 'Pages/GroupsOverlap/services/services'

import { INPUT_MESSAGES_MAX_ITEMS } from 'Constants'
import useAsyncSelect from 'Hooks/useAsyncSelect'

const InputFrom: FC = () => {
  const intl = useIntl()

  const { from, fromIsDirty } = useRecoilValue(searchState)

  const changeSearch = useRecoilCallback(changeSearchCallback, [])

  const { options, loading, handleSearch, hint, hintNoResults } =
    useAsyncSelect({
      key: 'groups-overlap-from',
      value: from,
      maxItems: INPUT_MESSAGES_MAX_ITEMS,
      fetchItems: async () => await fetchGroups(''),
      fetchItemsByTerm: fetchGroups,
      fetchItemById: fetchGroupById,
      labels: {
        loading: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusLoading',
        }),
        noResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusNoMatches',
        }),
        moreResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusTooManyItems',
        }),
      },
    })

  const handleExpandCollapse = useCallback(
    (isExpanded: boolean) => {
      if (!isExpanded) {
        handleSearch('')
      }
    },
    [handleSearch],
  )

  const handleChange = useCallback(
    (s: number | undefined) => {
      void changeSearch({
        from: s,
        fromIsDirty: true,
      })
    },
    [changeSearch],
  )

  return (
    <Select
      searchable
      value={from}
      options={options}
      loading={loading}
      onChange={handleChange}
      onSearch={handleSearch}
      onExpandCollapse={handleExpandCollapse}
      hint={hint}
      hintNoResults={hintNoResults}
      memoSelectOption
      placeholder={intl.formatMessage({
        id: 'Load.MailupCommon.Select',
      })}
      data-mu-test="groups-overlap__from"
      error={fromIsDirty && from === undefined}
    />
  )
}

export default InputFrom
